import React, { useCallback } from 'react';
import { fromDate, toCalendarDate } from '@internationalized/date';
import type { ApplyPendingFiltersCallback } from '../../../../types/search';
import { CALENDAR_DATE_FACET, CALENDAR_RESULTS_DATE_FACET } from '../..';
import { useDateFilter } from '../../contexts/PortalDateFilterProvider';
import { useCalendarViewMode } from '../../hooks/useCalendarViewMode';
import { useDateLabelFormatter } from '../../hooks/useDateLabelFormatter';
import SvgIcon from '../../../utils/SvgIcon';

type DateControlsProps = Readonly<{
  applyPendingFilters: ApplyPendingFiltersCallback;
  dateLabel: string;
  selectedValue: string;
}>;

export default function DateControls({ applyPendingFilters, dateLabel, selectedValue }: DateControlsProps) {
  const { setSelectedValue, setSelectedValueLabel } = useDateFilter(CALENDAR_DATE_FACET);
  const mode = useCalendarViewMode(selectedValue);
  const formatDateLabel = useDateLabelFormatter(selectedValue);

  const handleDateNavigation = useCallback(
    (direction: 'prev' | 'next') => {
      const [startDate, endDate] = selectedValue.split(',');
      const startCalendarDate = fromDate(new Date(startDate), 'UTC');
      const endCalendarDate = fromDate(new Date(endDate), 'UTC');

      // Determine navigation step based on mode and current month
      let dateUpdateConfig: { weeks?: number; months?: number; years?: number };
      if (mode === 'Weekly') {
        dateUpdateConfig = { weeks: 1 };
      } else {
        dateUpdateConfig = { years: 1 };
      }

      const operation = direction === 'prev' ? 'subtract' : 'add';
      const newDateFrom = toCalendarDate(startCalendarDate[operation](dateUpdateConfig)).toString();
      const newDateTo = toCalendarDate(endCalendarDate[operation](dateUpdateConfig)).toString();
      const newValue = [newDateFrom, newDateTo].join(',');
      const newLabel = formatDateLabel(newDateFrom, newDateTo);

      // Update the calendar date context
      setSelectedValue(newValue);
      setSelectedValueLabel(newLabel);

      // Apply filters using the same pattern as App.tsx
      // This updates both calendar dates and listing dateFrom/dateTo
      applyPendingFilters({
        [CALENDAR_DATE_FACET]: [newDateFrom, newDateTo],
        [CALENDAR_RESULTS_DATE_FACET]: null,
      });
    },
    [mode, selectedValue, setSelectedValue, setSelectedValueLabel, formatDateLabel, applyPendingFilters]
  );

  if (selectedValue === '') {
    return null;
  }

  // Determine aria-label text based on current navigation behavior
  const getNavigationLabel = (direction: 'prev' | 'next') => {
    if (mode === 'Weekly') return 'week';
    const [startDate] = selectedValue.split(',');
    const currentMonth = fromDate(new Date(startDate), 'UTC').month;
    const isJanuaryGoingBack = currentMonth === 1 && direction === 'prev';
    const isDecemberGoingForward = currentMonth === 12 && direction === 'next';
    return isJanuaryGoingBack || isDecemberGoingForward ? 'month' : 'year';
  };

  return (
    <div className="date-controls">
      <button
        onClick={() => handleDateNavigation('prev')}
        className="date-controls__btn"
        aria-label={`Move date range back by one ${getNavigationLabel('prev')}`}
      >
        <SvgIcon iconName="arrow-left-portal" additionalClasses="date-controls__icon" />
      </button>
      <p className="date-controls__date">{dateLabel}</p>
      <button
        onClick={() => handleDateNavigation('next')}
        className="date-controls__btn"
        aria-label={`Move date range forward by one ${getNavigationLabel('next')}`}
      >
        <SvgIcon iconName="arrow-right-portal" additionalClasses="date-controls__icon" />
      </button>
    </div>
  );
}
